import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  DEFAULT_WEEK_END_DAY,
  DEFAULT_WEEK_START_DAY,
  WEEK_START_DAY_OPTIONS,
  fetchTenantWorkWeek,
  formatWorkWeekRangeLabel,
  normalizeWeekEndDay,
  normalizeWeekStartDay,
  saveTenantWorkWeek,
} from '../WorkforceOperations/workforceWeekUtils';
import {
  harmonateFormFieldsSx,
  harmonateInputLabelProps,
  harmonateProceedButtonSx,
  harmonateSelectMenuProps,
  harmonateTextFieldSx,
} from '../../../theme/harmonateStyles';

const TenantWorkforceWeekSection = ({ open, tenantId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [weekStartDay, setWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);
  const [weekEndDay, setWeekEndDay] = useState(DEFAULT_WEEK_END_DAY);
  const [savedWeekStartDay, setSavedWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);
  const [savedWeekEndDay, setSavedWeekEndDay] = useState(DEFAULT_WEEK_END_DAY);

  const previewLabel = useMemo(
    () => formatWorkWeekRangeLabel(weekStartDay, weekEndDay),
    [weekStartDay, weekEndDay]
  );
  const dirty = weekStartDay !== savedWeekStartDay || weekEndDay !== savedWeekEndDay;

  useEffect(() => {
    if (!open || !tenantId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const week = await fetchTenantWorkWeek(process.env.REACT_APP_BACKEND_URL, token, tenantId);
        if (cancelled) return;
        const start = normalizeWeekStartDay(week.weekStartDay);
        const end = normalizeWeekEndDay(week.weekEndDay);
        setWeekStartDay(start);
        setWeekEndDay(end);
        setSavedWeekStartDay(start);
        setSavedWeekEndDay(end);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load work week');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, tenantId]);

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const saved = await saveTenantWorkWeek(
        process.env.REACT_APP_BACKEND_URL,
        token,
        tenantId,
        weekStartDay,
        weekEndDay
      );
      setSavedWeekStartDay(saved.weekStartDay);
      setSavedWeekEndDay(saved.weekEndDay);
      if (onSaved) onSaved(`Workforce work week saved: ${formatWorkWeekRangeLabel(saved.weekStartDay, saved.weekEndDay)}`);
    } catch (err) {
      setError(err.message || 'Failed to save work week');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonateFormFieldsSx, p: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Sets the work week for every property in this tenant. Week view and position coverage show only
        these days. Month view still shows the full calendar.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          select
          label="Work week starts on"
          size="small"
          fullWidth
          value={weekStartDay}
          onChange={(e) => setWeekStartDay(e.target.value)}
          InputLabelProps={harmonateInputLabelProps}
          SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          sx={harmonateTextFieldSx}
        >
          {WEEK_START_DAY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Work week ends on"
          size="small"
          fullWidth
          value={weekEndDay}
          onChange={(e) => setWeekEndDay(e.target.value)}
          InputLabelProps={harmonateInputLabelProps}
          SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          sx={harmonateTextFieldSx}
        >
          {WEEK_START_DAY_OPTIONS.map((option) => (
            <MenuItem key={`end-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Typography variant="body2" sx={{ mt: 1.5 }}>
        Week view will be <strong>{previewLabel}</strong>
      </Typography>
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : null}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !dirty}
          sx={harmonateProceedButtonSx}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default TenantWorkforceWeekSection;
